import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import Header, { Action } from '../shared/TailwindComponents/Header';
import FilterBarWithTabs, { TabItem } from '../shared/TailwindComponents/FilterBarWithTabs';
import FinanceOverview from './FinanceOverview';
import FinanceHelpModal from './FinanceHelpModal';
import BankAccountTab from './BankAccountTab';
import TransactionsTab from './TransactionsTab';
import InvoicesTab from './InvoicesTab';
import PayoutsTab from './PayoutsTab';
import ExpensesTab from './ExpensesTab';
import BillingRatesTab from './BillingRatesTab';
import {
  fetchFinanceAccount,
  fetchBalance,
  fetchFinancialSummary,
  connectBankAccount,
  createStripeDashboardLink,
  syncTransactions,
  syncPayouts,
  triggerPayout,
} from '../../store/actions/FinanceActions';
import {
  PiBankLight,
  PiPlusLight,
  PiArrowSquareOutLight,
  PiArrowsClockwiseLight,
  PiArrowUpLight,
  PiReceiptLight,
  PiScalesLight,
  PiQuestionLight,
} from 'react-icons/pi';

const VALID_TABS = ['overview', 'bank', 'transactions', 'invoices', 'payouts', 'expenses', 'rates'];

const FinanceDashboard: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { tab } = useParams<{ tab?: string }>();

  const activeTab = VALID_TABS.includes(tab || '') ? tab! : 'overview';

  const userInfo = useAppSelector((state: RootState) => state.user.userInfo);
  const account = useAppSelector((state: RootState) => state.finance.account);
  const balance = useAppSelector((state: RootState) => state.finance.balance);
  const loading = useAppSelector((state: RootState) => state.finance.loading);
  const organizationId = userInfo?.organization_id || userInfo?.OrganizationID;

  const [helpModalOpen, setHelpModalOpen] = useState(false);

  const breadcrumbs = [
    { name: t('Finance'), href: '/finance', current: true },
  ];

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchFinanceAccount(organizationId));
      dispatch(fetchBalance(organizationId));
      dispatch(fetchFinancialSummary(organizationId));
    }
  }, [dispatch, organizationId]);

  const tabs: TabItem[] = [
    { label: t('Overview'), value: 'overview' },
    { label: t('Bank account'), value: 'bank' },
    { label: t('Transactions'), value: 'transactions' },
    { label: t('Invoices'), value: 'invoices' },
    { label: t('Expenses'), value: 'expenses' },
    { label: t('Rates'), value: 'rates' },
    { label: t('Payouts'), value: 'payouts' },
  ];

  const handleTabClick = useCallback((tabValue: string) => {
    history.push(tabValue === 'overview' ? '/finance' : `/finance/${tabValue}`);
  }, [history]);

  // ── Connect handler ────────────────────────────────────

  const handleConnect = useCallback(async () => {
    if (!organizationId) return;
    const result = await dispatch(
      connectBankAccount({
        organization_id: organizationId,
        email: userInfo?.email,
        business_name: userInfo?.organization_name,
        country: 'US',
        refresh_url: `${window.location.origin}/finance`,
        return_url: `${window.location.origin}/finance`,
      })
    );
    if (result?.onboarding_url) {
      window.location.href = result.onboarding_url;
    }
  }, [dispatch, organizationId, userInfo]);

  // ── Per-tab header actions ─────────────────────────────

  const headerActions: Action[] = useMemo(() => {
    // Help icon - always visible
    const helpAction: Action = {
      label: '',
      icon: <PiQuestionLight className="w-4 h-4" />,
      onClick: () => setHelpModalOpen(true),
      variant: 'outline' as const,
    };

    // Global: always show Connect or Stripe Dashboard
    const globalActions: Action[] = account
      ? [
          {
            label: t('Stripe dashboard'),
            icon: <PiArrowSquareOutLight className="w-4 h-4" />,
            onClick: async () => {
              if (!organizationId) return;
              const result = await dispatch(createStripeDashboardLink(organizationId));
              if (result?.url) window.open(result.url, '_blank');
            },
            variant: 'outline' as const,
          },
          helpAction,
        ]
      : [
          {
            label: t('Connect bank account'),
            icon: <PiBankLight className="w-4 h-4" />,
            onClick: handleConnect,
            variant: 'primary' as const,
          },
          helpAction,
        ];

    // Tab-specific actions
    switch (activeTab) {
      case 'transactions':
        return [
          {
            label: t('Sync'),
            icon: <PiArrowsClockwiseLight className="w-4 h-4" />,
            onClick: async () => {
              if (!organizationId) return;
              await dispatch(syncTransactions(organizationId));
            },
            variant: 'outline' as const,
            disabled: !account,
          },
          ...globalActions,
        ];

      case 'invoices':
        return [
          {
            label: t('New invoice'),
            icon: <PiPlusLight className="w-4 h-4" />,
            onClick: () => {
              // Dispatch a custom event that InvoicesTab listens to
              window.dispatchEvent(new CustomEvent('finance:new-invoice'));
            },
            variant: 'primary' as const,
            disabled: !account?.charges_enabled,
          },
          ...globalActions,
        ];

      case 'payouts': {
        const availableBalance = balance?.available?.[0]?.amount || 0;
        return [
          {
            label: t('Sync'),
            icon: <PiArrowsClockwiseLight className="w-4 h-4" />,
            onClick: async () => {
              if (!organizationId) return;
              await dispatch(syncPayouts(organizationId));
            },
            variant: 'outline' as const,
            disabled: !account,
          },
          {
            label: t('Manual payout'),
            icon: <PiArrowUpLight className="w-4 h-4" />,
            onClick: () => {
              window.dispatchEvent(new CustomEvent('finance:manual-payout'));
            },
            variant: 'primary' as const,
            disabled: !account?.payouts_enabled || availableBalance <= 0,
          },
          ...globalActions,
        ];
      }

      case 'expenses':
        return [
          {
            label: t('New expense'),
            icon: <PiReceiptLight className="w-4 h-4" />,
            onClick: () => {
              window.dispatchEvent(new CustomEvent('finance:new-expense'));
            },
            variant: 'primary' as const,
          },
          ...globalActions,
        ];

      case 'rates':
        return [
          {
            label: t('New rate'),
            icon: <PiScalesLight className="w-4 h-4" />,
            onClick: () => {
              window.dispatchEvent(new CustomEvent('finance:new-rate'));
            },
            variant: 'primary' as const,
          },
          ...globalActions,
        ];

      default:
        return globalActions;
    }
  }, [activeTab, account, balance, organizationId, dispatch, handleConnect, t]);

  const renderTab = () => {
    switch (activeTab) {
      case 'overview':
        return <FinanceOverview organizationId={organizationId} />;
      case 'bank':
        return <BankAccountTab organizationId={organizationId} />;
      case 'transactions':
        return <TransactionsTab organizationId={organizationId} />;
      case 'invoices':
        return <InvoicesTab organizationId={organizationId} />;
      case 'payouts':
        return <PayoutsTab organizationId={organizationId} />;
      case 'expenses':
        return <ExpensesTab organizationId={organizationId} />;
      case 'rates':
        return <BillingRatesTab organizationId={organizationId} />;
      default:
        return <FinanceOverview organizationId={organizationId} />;
    }
  };

  return (
    <div className="flex flex-col h-full bg-background-light dark:bg-background-dark">
      <Header breadcrumbs={breadcrumbs} actions={headerActions} />
      <FilterBarWithTabs tabs={tabs} activeTab={activeTab} onTabClick={handleTabClick} />
      <div className="flex-1 overflow-y-auto">
        {renderTab()}
      </div>
      <FinanceHelpModal isOpen={helpModalOpen} onClose={() => setHelpModalOpen(false)} />
    </div>
  );
};

export default FinanceDashboard;
